import { useState, useRef, useEffect } from "react";
import { ChevronDown, Globe } from "lucide-react";
import { createPortal } from "react-dom";
import { useLanguage } from "@/context/LanguageContext";
import { languageNamesLabels } from "@/i18n";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Language } from "@/utils/languageUtils";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import {
  NativeOptionSheet,
  type NativeOption,
} from "@/components/native/NativeOptionSheet";

type LanguageSwitcherProps = {
  /** Portal dropdown alignment (use `end` when trigger is on the right edge). */
  dropdownAlign?: "start" | "end";
};

const LanguageSwitcher = ({ dropdownAlign = "end" }: LanguageSwitcherProps) => {
  const { language, setLanguage } = useLanguage();
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const [isOpen, setIsOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left?: number;
    right?: number;
  }>({
    top: 0,
    right: 0,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const langNames = languageNamesLabels(language);

  const languageOptions: NativeOption[] = (["sv", "en"] as const).map(
    (lang) => ({
      value: lang,
      label: langNames[lang],
    }),
  );

  const handleMouseEnter = () => {
    if (isMobile || isNative) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (isMobile || isNative) return;
    timeoutRef.current = setTimeout(() => setIsOpen(false), 100);
  };

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      if (dropdownAlign === "start") {
        setDropdownPosition({
          top: rect.bottom + 4,
          left: Math.max(8, rect.left),
          right: undefined,
        });
      } else {
        setDropdownPosition({
          top: rect.bottom + 4,
          right: Math.max(8, window.innerWidth - rect.right),
          left: undefined,
        });
      }
    }
  }, [isOpen, dropdownAlign]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !isMobile || isNative) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !dropdownRef.current?.contains(target) &&
        !contentRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen, isMobile, isNative]);

  const DropdownContent = () => (
    <div
      ref={contentRef}
      className="fixed w-40 bg-white dark:bg-zinc-950 border border-gray-200 dark:border-white/15 rounded-md shadow-xl z-[100] py-1"
      style={{
        top: dropdownPosition.top,
        ...(dropdownPosition.left != null
          ? { left: dropdownPosition.left }
          : { right: dropdownPosition.right }),
      }}
      onMouseEnter={!isMobile ? handleMouseEnter : undefined}
      onMouseLeave={!isMobile ? handleMouseLeave : undefined}
      onClick={(e) => e.stopPropagation()}
    >
      {(["sv", "en"] as const).map((lang) => (
        <button
          key={lang}
          onClick={() => {
            setLanguage(lang);
            setIsOpen(false);
          }}
          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-white/10 ${
            language === lang ? "bg-gray-100 dark:bg-white/10" : ""
          }`}
        >
          {langNames[lang]}
        </button>
      ))}
    </div>
  );

  const triggerButton = (
    <button
      type="button"
      onClick={() => {
        if (isNative) {
          setSheetOpen(true);
          return;
        }
        if (isMobile) {
          setIsOpen(!isOpen);
        }
      }}
      className="flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-accent/10 transition-colors text-sm"
      aria-label="Change language"
    >
      <Globe className="h-4 w-4" />
      <span className="hidden sm:inline">{langNames[language]}</span>
      {!isNative && <ChevronDown className="h-3 w-3" />}
    </button>
  );

  if (isNative) {
    return (
      <>
        {triggerButton}
        <NativeOptionSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title={langNames[language]}
          value={language}
          options={languageOptions}
          onSelect={(lang) => {
            setLanguage(lang as Language);
            setSheetOpen(false);
          }}
        />
      </>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {triggerButton}
      {isOpen && createPortal(<DropdownContent />, document.body)}
    </div>
  );
};

export default LanguageSwitcher;
